import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ItemGroup } from '@/components/ui/item';
import { toAttachmentSummary, useReceiveAttachmentDownload, type ReceiveReleaseResultDto } from '@/entities/receive';
import { AttachmentItem } from '@/features/consignment-attachment-list';
import { ConfirmArrivalWarnings } from '@/features/receive-confirm-arrival';

interface ReleaseGoodsResultProps {
  result: ReceiveReleaseResultDto;
}

/** What the release wrote — the release note numbers above all, since nothing prints them yet. */
export function ReleaseGoodsResult({ result }: ReleaseGoodsResultProps) {
  const { t } = useTranslation();
  const { download } = useReceiveAttachmentDownload();

  return (
    <div className="space-y-4">
      <Alert variant="success">
        <CheckCircle2 />
        <AlertTitle>{t('receive.release.success', 'Goods released')}</AlertTitle>
      </Alert>

      {result.goodsReleaseNoteNumbers.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('release.note.number.label', 'Goods Released Note Number')}
          </p>
          <div className="flex flex-wrap gap-2">
            {result.goodsReleaseNoteNumbers.map((number) => (
              <Badge key={number} variant="outline" className="font-mono">
                {number}
              </Badge>
            ))}
          </div>
          {result.printGoodsReleaseNote && (
            <p className="text-xs text-muted-foreground">
              {t(
                'receive.release.result.printPending',
                'Printing the Goods Release Note is not available yet — note the number above.'
              )}
            </p>
          )}
        </div>
      )}

      {result.attachments?.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t('newcmr.attachments.short', 'Attachments')}</p>
          <ItemGroup>
            {result.attachments.map((attachment) => (
              <AttachmentItem
                key={`${attachment.shipmentId}-${attachment.id}`}
                attachment={toAttachmentSummary(attachment)}
                onDownload={() => download(attachment)}
              />
            ))}
          </ItemGroup>
        </div>
      )}

      {result.consignmentStatusChanges.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('receive.confirm.result.statusChanges', 'Consignment status changes')}
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {result.consignmentStatusChanges.map((change) => (
              <li key={change.consignmentId}>
                {change.consignmentNumber}: {change.fromStatusCode} → {change.toStatusCode}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmArrivalWarnings warnings={result.warnings} />
    </div>
  );
}
